import { useEffect, useMemo, useRef, useState } from 'react'
import { User } from 'lucide-react'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'
import { formatDate } from '../lib/dates.js'

/**
 * Putting somebody on a day, or changing the note beside their name — a popover anchored to
 * the day or the chip that was clicked, like the board's own editor and for the same reason:
 * a form inside the cell would resize the row under the pointer.
 *
 * Adding is two steps in one panel: find the person, then say anything about them. The name
 * box has focus first because it is the thing that varies; Enter on a match chooses it and
 * moves on to the note, and Enter again adds. Somebody putting six people on Thursday never
 * touches the mouse. Mounted fresh for every open — the grid keys it by what it is editing —
 * so there is no state to reset.
 *
 * Every handler returns whether the write landed, and the panel closes only on a yes, so a
 * save the server refused leaves what was typed on screen with the reason beside it.
 */
export default function ListEntryEditor({
  anchorRef,
  mode, // 'add' | 'edit'
  date,
  people = [], // who could be added — already narrowed to those not on this day
  entry = null,
  onAdd, // ({ userId, note }) => Promise<boolean>
  onSave, // (note) => Promise<boolean>
  onRemove, // () => Promise<boolean>
  onClose,
}) {
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState(null)
  const [note, setNote] = useState(entry?.note || '')
  const [active, setActive] = useState(0)
  const [busy, setBusy] = useState(false)
  const noteRef = useRef(null)

  const needle = query.trim().toLowerCase()
  const shown = useMemo(
    () => people.filter((p) => !needle || p.name.toLowerCase().includes(needle)).slice(0, 8),
    [people, needle],
  )
  useEffect(() => { setActive(0) }, [needle])
  // The note is the second step; focus moves there the moment the first is done.
  useEffect(() => { if (picked) noteRef.current?.focus() }, [picked])

  async function submit(e) {
    e?.preventDefault()
    if (busy) return
    setBusy(true)
    const ok = mode === 'add' ? Boolean(picked) && (await onAdd({ userId: picked.id, note: note.trim() })) : await onSave(note.trim())
    setBusy(false)
    if (ok) onClose()
  }

  async function remove() {
    if (busy) return
    setBusy(true)
    const ok = await onRemove()
    setBusy(false)
    if (ok) onClose()
  }

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(shown.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (shown[active]) setPicked(shown[active])
    }
  }

  const heading = mode === 'add' ? `Add to ${formatDate(date)}` : entry?.userName || 'Note'

  return (
    <Popover
      open
      anchorRef={anchorRef}
      close={onClose}
      className="list-pop"
      label={heading}
      align="left"
      placeKey={`${shown.length}:${Boolean(picked)}`}
      autoFocus
    >
      <div className="list-pop-head">
        <strong>{heading}</strong>
        {mode === 'edit' && entry && <span className="sub">{formatDate(entry.date)}</span>}
      </div>

      {mode === 'add' && !picked && (
        <>
          <div className="filter-panel-search">
            <Icon name="search" size={13} />
            <input
              type="text"
              value={query}
              placeholder="Who?"
              aria-label="Who?"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
            />
          </div>
          <div className="dd-items list-pop-people" role="listbox" aria-label="People">
            {shown.map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={i === active}
                className={`ctx-item ${i === active ? 'active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => setPicked(p)}
              >
                <span className="ctx-item-icon">
                  <User size={14} aria-hidden="true" />
                </span>
                <span>{p.name}</span>
              </button>
            ))}
            {shown.length === 0 && (
              <div className="ctx-label">
                {people.length ? 'Nobody matches' : 'Everybody is already on this day'}
              </div>
            )}
          </div>
        </>
      )}

      {(picked || mode === 'edit') && (
        <form className="list-pop-form" onSubmit={submit}>
          {picked && (
            <div className="list-pop-picked">
              <User size={14} aria-hidden="true" />
              <strong>{picked.name}</strong>
              <button type="button" className="btn sm ghost" onClick={() => setPicked(null)}>
                Change
              </button>
            </div>
          )}
          <input
            ref={noteRef}
            type="text"
            className="list-pop-note"
            maxLength={200}
            value={note}
            placeholder="A note, if one is wanted"
            aria-label="Note"
            onChange={(e) => setNote(e.target.value)}
            // Handled here rather than left to the form's implicit submission, the way the
            // search box above handles its own Enter: "Enter to add" is the promise the hint
            // makes, and it should not depend on which key event a browser turns into a
            // submit. preventDefault keeps the form from submitting a second time.
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              }
            }}
          />
          <div className="cell-pop-foot">
            {mode === 'edit' ? (
              <button type="button" className="btn sm ghost danger" onClick={remove} disabled={busy}>
                <Icon name="trash" size={14} />
                Remove
              </button>
            ) : (
              <span className="cell-pop-hint">Enter to add</span>
            )}
            <span className="spacer" />
            <button type="button" className="btn sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn sm primary" disabled={busy}>
              {mode === 'add' ? 'Add' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </Popover>
  )
}
